import { ArrowRight, Shield, Users, TrendingUp, Calculator, PieChart, Award, CheckCircle, Star, BarChart, Clock, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import OptimizedImage from './OptimizedImage';
import { FadeIn } from '../hooks/useScrollAnimation';
import { AnimatedCounter } from '../hooks/useCounterAnimation';
import SEO from './SEO';
import { pageSEO, businessJsonLd } from '../seoData';

const Home = () => {
  return (
    <div className="min-h-screen">
      <SEO {...pageSEO.home} jsonLd={businessJsonLd} />
      {/* Hero Section - Clean modern design with image on right */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-20 pb-32 overflow-visible">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="mb-6">
                <span className="inline-block bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide">
                  TRUSTED SINCE 1996 • 5000+ SATISFIED CLIENTS
                </span>
              </div>
              <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                Build Wealth.<br/>
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Secure Future.</span>
              </h1>
              <p className="text-xl lg:text-2xl text-gray-600 mb-8 leading-relaxed">
                Expert investment guidance for Indians and NRIs. Transform systematic savings into lasting wealth through proven strategies and professional portfolio management.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link
                  to="/book-consultation"
                  className="btn-hover bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 inline-flex items-center justify-center shadow-xl"
                >
                  Get Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/calculator"
                  className="btn-hover border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-xl font-bold text-lg inline-flex items-center justify-center"
                >
                  <Calculator className="mr-2 h-5 w-5" />
                  Calculate Returns
                </Link>
              </div>
            </div>
            
            {/* Right Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <OptimizedImage 
                  src="https://images.unsplash.com/photo-1713461983836-de0a45009424?w=800&q=85" 
                  alt="SEBI registered financial advisor in India - Professional investment planning with calculator, stock market charts, and portfolio analysis for wealth creation"
                  className="w-full h-[500px]"
                  priority={true}
                  objectFit="cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/10 to-purple-600/10"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Trust Indicators - Below hero */}
        <div className="absolute -bottom-12 left-0 right-0 z-10 hidden md:block">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <div className="grid grid-cols-3 gap-6 bg-white rounded-2xl p-6 shadow-2xl border border-gray-100">
                <div className="text-center">
                  <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    ₹<AnimatedCounter value={500} suffix="Cr+" />
                  </div>
                  <div className="text-gray-600 text-sm">Assets Under Management</div>
                </div>
                <div className="text-center border-l border-r border-gray-200">
                  <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    <AnimatedCounter value={5000} suffix="+" />
                  </div>
                  <div className="text-gray-600 text-sm">Happy Clients</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Since 1996</div>
                  <div className="text-gray-600 text-sm">Trusted Legacy</div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
        
        {/* Mobile Trust Indicators - Stack vertically */}
        <div className="md:hidden mt-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <div className="space-y-4 bg-white rounded-2xl p-6 shadow-2xl border border-gray-100">
                <div className="text-center pb-4 border-b border-gray-200">
                  <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1">
                    ₹<AnimatedCounter value={500} suffix="Cr+" />
                  </div>
                  <div className="text-gray-600 text-sm">Assets Under Management</div>
                </div>
                <div className="text-center pb-4 border-b border-gray-200">
                  <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1">
                    <AnimatedCounter value={5000} suffix="+" />
                  </div>
                  <div className="text-gray-600 text-sm">Happy Clients</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1">Since 1996</div>
                  <div className="text-gray-600 text-sm">Trusted Legacy</div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Professional Certifications & Registrations */}
      <section className="py-16 bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-3">
                Trusted & Regulated Financial Services
              </h2>
              <p className="text-blue-100 text-lg">
                Fully licensed and compliant with regulatory authorities
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {/* SEBI Registered Sub Broker */}
              <FadeIn delay={100}>
                <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-2xl p-8 text-center hover:bg-white/20 transition-all duration-300 card-lift">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Shield className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">SEBI Registered</h3>
                  <p className="text-blue-100 text-lg font-semibold mb-2">Sub Broker</p>
                  <p className="text-blue-200 text-sm">
                    Equity Broking Services
                  </p>
                  <div className="mt-4 pt-4 border-t border-white/20">
                    <p className="text-xs text-blue-300">NSE: AP041204383</p>
                    <p className="text-xs text-blue-300">BSE: AP0102910174480</p>
                  </div>
                </div>
              </FadeIn>

              {/* AMFI Registered */}
              <FadeIn delay={200}>
                <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-2xl p-8 text-center hover:bg-white/20 transition-all duration-300 card-lift">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Award className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">AMFI Registered</h3>
                  <p className="text-blue-100 text-lg font-semibold mb-2">Mutual Fund Distributor</p>
                  <p className="text-blue-200 text-sm">
                    Authorized MF Distribution Services
                  </p>
                  <div className="mt-4 pt-4 border-t border-white/20">
                    <p className="text-xs text-blue-300">AMFI REG NO: 23639</p>
                  </div>
                </div>
              </FadeIn>

              {/* PMS License Holder */}
              <FadeIn delay={300}>
                <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-2xl p-8 text-center hover:bg-white/20 transition-all duration-300 card-lift">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <CheckCircle className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Licensed</h3>
                  <p className="text-blue-100 text-lg font-semibold mb-2">PMS Distribution</p>
                  <p className="text-blue-200 text-sm">
                    Portfolio Management Services
                  </p>
                  <div className="mt-4 pt-4 border-t border-white/20">
                    <p className="text-xs text-blue-300">PMS APRN CODE: APRN00365</p>
                  </div>
                </div>
              </FadeIn>
            </div>
            
            <div className="text-center mt-10">
              <p className="text-blue-100 text-sm">
                <Shield className="inline h-4 w-4 mr-2" />
                All registrations verified and compliant with regulatory requirements
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Value Proposition - Clean Section */}
      <section className="py-20 pt-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Why Choose Rumtek Finvest?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Your trusted partner in wealth creation with proven expertise and personalized approach
              </p>
            </div>
          </FadeIn>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FadeIn delay={100}>
              <div className="card-lift text-center p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-white shadow-lg">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Client-First Approach</h3>
                <p className="text-gray-600 leading-relaxed">
                  Your financial goals are our priority. Dedicated relationship managers provide personalized attention to every client.
                </p>
              </div>
            </FadeIn>
            
            <FadeIn delay={200}>
              <div className="card-lift text-center p-8 rounded-2xl bg-gradient-to-br from-green-50 to-white shadow-lg">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Goal-Based Planning</h3>
                <p className="text-gray-600 leading-relaxed">
                  Customized investment strategies aligned with your specific goals, risk tolerance, and time horizon.
                </p>
              </div>
            </FadeIn>
            
            <FadeIn delay={300}>
              <div className="card-lift text-center p-8 rounded-2xl bg-gradient-to-br from-purple-50 to-white shadow-lg">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Proven Track Record</h3>
                <p className="text-gray-600 leading-relaxed">
                  Decades of experience helping 5000+ clients achieve their financial dreams with consistent performance.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* The Philosophy - Visual Split Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              The Wealth Creation Philosophy
            </h2>
            <p className="text-xl text-gray-600">
              A perfect synergy of disciplined saving and strategic investing
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* The ART */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow">
              <div className="h-64 overflow-hidden">
                <OptimizedImage 
                  src="https://images.unsplash.com/photo-1659355894117-0ae6f8f28d0b?w=800&q=85" 
                  alt="Financial advisor India analyzing investment documents - Systematic wealth creation through disciplined saving and smart portfolio management"
                  className="w-full h-full transform hover:scale-105 transition-transform duration-500"
                  objectFit="cover"
                />
              </div>
              <div className="p-8">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                    A
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">The ART</h3>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Save first, spend later. The discipline of setting aside money every month before expenses creates the foundation for wealth. It's not about how much you earn, but how much you save.
                </p>
                <Link 
                  to="/about" 
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold text-lg group"
                >
                  Learn More 
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>
            </div>
            
            {/* The SCIENCE */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow">
              <div className="h-64 overflow-hidden">
                <OptimizedImage 
                  src="https://images.pexels.com/photos/8353821/pexels-photo-8353821.jpeg?w=800&q=85" 
                  alt="Wealth management services India - Professional portfolio management consultation for mutual funds, equity, and retirement planning"
                  className="w-full h-full transform hover:scale-105 transition-transform duration-500"
                  objectFit="cover"
                />
              </div>
              <div className="p-8">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                    S
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">The SCIENCE</h3>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Strategic asset allocation. Invest your savings in a diversified portfolio of equity, debt, and alternative assets. Professional management and rebalancing maximize returns.
                </p>
                <Link 
                  to="/services" 
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold text-lg group"
                >
                  Explore Services 
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wealth Creation Calculator - Interactive Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              Your Path To Financial Freedom
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              See how systematic investing of ₹1,00,000 per month can create a life-changing corpus in just 8 years
            </p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 lg:p-12 border border-white/20">
            <div className="text-center mb-8">
              <div className="text-2xl font-bold mb-2">Monthly Investment</div>
              <div className="text-5xl font-bold text-yellow-400">₹1,00,000</div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-8 text-center">
                <div className="text-sm uppercase tracking-wide mb-2">Conservative @ 8%</div>
                <div className="text-4xl font-bold mb-4">₹1.35 Cr</div>
                <div className="text-sm mb-4">Corpus after 8 years</div>
                <div className="border-t border-white/30 pt-4">
                  <div className="text-sm">Monthly Income</div>
                  <div className="text-3xl font-bold">₹89,841</div>
                  <div className="text-xs mt-2">For life, while corpus keeps growing</div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-8 text-center">
                <div className="text-sm uppercase tracking-wide mb-2">Growth @ 10%</div>
                <div className="text-4xl font-bold mb-4">₹1.47 Cr</div>
                <div className="text-sm mb-4">Corpus after 8 years</div>
                <div className="border-t border-white/30 pt-4">
                  <div className="text-sm">Monthly Income</div>
                  <div className="text-3xl font-bold">₹1,22,833</div>
                  <div className="text-xs mt-2">For life, while corpus keeps growing</div>
                </div>
              </div>
            </div>
            
            <div className="text-center bg-yellow-400/20 rounded-xl p-6 mb-8">
              <p className="text-lg font-semibold mb-2">THIS IS MATHEMATICS.</p>
              <p className="text-sm text-blue-100">
                Assumptions: Corpus stays intact. Monthly withdrawals are earnings only. Amount keeps growing at same rate.
              </p>
            </div>
            
            <div className="text-center">
              <Link
                to="/calculator"
                className="bg-white text-blue-900 px-10 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all transform hover:scale-105 inline-flex items-center shadow-2xl"
              >
                <Calculator className="mr-2 h-6 w-6" />
                Calculate Your Wealth Journey
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Client Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from real people. Join 5000+ satisfied investors who achieved their financial dreams.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow">
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 text-lg leading-relaxed italic">
                "Started with ₹25,000 monthly SIP 8 years ago. Today my portfolio is worth ₹1.2 crores! Rumtek's systematic approach really works."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                  RK
                </div>
                <div>
                  <div className="font-bold text-gray-900">Rajesh Kumar</div>
                  <div className="text-sm text-gray-600">IT Professional, Mumbai</div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow">
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 text-lg leading-relaxed italic">
                "Their retirement planning helped me retire at 55 with a comfortable corpus. Expert guidance made all the difference."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                  PS
                </div>
                <div>
                  <div className="font-bold text-gray-900">Priya Sharma</div>
                  <div className="text-sm text-gray-600">Entrepreneur, Bangalore</div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow">
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 text-lg leading-relaxed italic">
                "PMS service has given me 16% annual returns consistently. Their research and stock selection is outstanding."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                  AM
                </div>
                <div>
                  <div className="font-bold text-gray-900">Anil Mehta</div>
                  <div className="text-sm text-gray-600">Business Owner, Kolkata</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview - Card Style */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Comprehensive Investment Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From equity trading to portfolio management - complete financial services under one roof
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Equity Trading</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Direct equity investments in fundamentally strong companies. SEBI registered advisor with NSE/BSE membership.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Research reports & technical analysis</span>
                </li>
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>SIP in direct stocks</span>
                </li>
              </ul>
              <div className="text-sm font-semibold text-blue-600 mb-2">Min Investment: ₹1 Lakh</div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6">
                <PieChart className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Mutual Funds</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Diversified mutual fund portfolios across all major AMCs. Systematic investment planning for all risk profiles.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Quarterly tracking & rebalancing</span>
                </li>
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Mobile app for daily tracking</span>
                </li>
              </ul>
              <div className="text-sm font-semibold text-green-600 mb-2">Min Investment: ₹5,000/month</div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Portfolio Management</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Professional PMS services with customized strategies and dedicated relationship managers.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Top-performing fund managers</span>
                </li>
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Direct equity ownership</span>
                </li>
              </ul>
              <div className="text-sm font-semibold text-purple-600 mb-2">Min Investment: ₹50 Lakhs</div>
            </div>
          </div>
          
          <div className="text-center">
            <Link
              to="/services"
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 inline-flex items-center shadow-xl"
            >
              View All Services
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA - Strong */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-purple-900 to-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready To Start Your Wealth Journey?
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Book a free consultation with our expert advisors. Let's create a personalized investment strategy for your financial goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/book-consultation"
              className="bg-white text-blue-900 px-10 py-5 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-2xl"
            >
              Book Free Consultation
              <ArrowRight className="ml-2 h-6 w-6" />
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-10 py-5 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center"
            >
              Contact Us
            </Link>
          </div>
          
          <div className="mt-12 pt-8 border-t border-white/20">
            <div className="flex flex-col md:flex-row items-center justify-center md:space-x-8 space-y-4 md:space-y-0 text-blue-100">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" />
                <span>Multi-Asset Investment Options</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" />
                <span>Transparent Fee Structure</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" />
                <span><AnimatedCounter value={5000} suffix="+ Happy Clients" duration={2500} /></span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
